import { durationToSeconds } from "@multimediallc/web-utils"
import { isiOS } from "@multimediallc/web-utils/modernizr"
import { addEventListenerPoly, removeEventListenerPoly } from "./addEventListenerPolyfill"
import { Debouncer, DebounceTypes } from "./debouncer"
import { documentClickEvent } from "./dropDownComponentBase"
import { ListenerGroup } from "./events"
import { addPageAction } from "./newrelic"
import { ignoreCatch } from "./promiseUtils"
import { documentVisibilityChange } from "./windowUtils"

/**
 * Note that WakeLock will only work in secure contexts (https)
 * For more information and a list of supported browsers:
 * https://developer.mozilla.org/en-US/docs/Web/API/WakeLock
 */
export class WakeLock {
    private static instance?: WakeLock
    private wakeLock: WakeLockSentinel | undefined
    private attemptRequestDebouncer: Debouncer
    private numFailedAttempts: number
    private maxFailedAttempts: number
    private cooldownDuration: number
    private listenerGroup: ListenerGroup

    constructor() {
        this.attemptRequestDebouncer = new Debouncer(() => {
            this.attemptRequest()
        }, { bounceLimitMS: 200, debounceType: DebounceTypes.debounce })
        this.numFailedAttempts = 0
        this.maxFailedAttempts = 5
        this.cooldownDuration = durationToSeconds({ minutes: 5 }) * 1000
        this.listenerGroup = new ListenerGroup()
    }

    public static start(): void {
        if (WakeLock.instance === undefined) {
            WakeLock.instance = new WakeLock()
            WakeLock.instance.initListeners()

            if (!isiOS()) { // iOS will initialize request in click listener
                WakeLock.instance.attemptRequest()
            }
        }
    }

    public static stop(): void {
        WakeLock.instance?.cleanup()
        WakeLock.instance = undefined
    }

    private initListeners(): void {
        if (!this.isSupported()) {
            return
        }

        // iOS can only request a new wakelock when in close succession with a
        // user click, unless in `documentVisibilityChange`.
        // This also acts as a retry strategy for when the request fails
        // on any device.
        documentClickEvent.listen(() => {
            this.attemptRequestDebouncer.callFunc()
        }).addTo(this.listenerGroup)

        // We need to re-request wakelock whenever the document goes from
        // not visible -> visible (e.g. switching tabs) because the wakelock
        // will have been released.
        // iOS does not need a user click for this case as long as it has received
        // a wakelock previously.
        documentVisibilityChange.listen((isVisible: boolean) => {
            if (isVisible) {
                this.attemptRequestDebouncer.callFunc()
            }
        }).addTo(this.listenerGroup)

        addEventListenerPoly("pageshow", window, this.onPageshow)
        addEventListenerPoly("pagehide", window, this.onPagehide)
    }

    private attemptRequest(): void {
        const hasActiveWakeLock = this.wakeLock?.released === false
        const canReattempt = this.numFailedAttempts < this.maxFailedAttempts

        if (!this.isSupported() || hasActiveWakeLock || !canReattempt) {
            return
        }

        navigator.wakeLock // eslint-disable-line compat/compat
            .request("screen")
            .then((wakeLock) => {
                this.wakeLock = wakeLock
            })
            .catch((err: DOMException) => {
                const errString = `${err.name}: ${err.message}`
                addPageAction("WakeLockFailed", { error: errString })
                debug(`Error requesting wakelock: ${errString}`)
                this.numFailedAttempts += 1

                if (this.numFailedAttempts === this.maxFailedAttempts) {
                    window.setTimeout(() => this.numFailedAttempts = 0, this.cooldownDuration)
                }
            })
    }

    private releaseLock(): void {
        this.wakeLock?.release().catch(ignoreCatch)
    }

    private onPageshow = (event: PageTransitionEvent): void => {
        // Re-request if coming back from bfcache
        if (event.persisted && this.wakeLock?.released === true) {
            this.attemptRequest()
        }
    }

    private onPagehide = (): void => {
        this.releaseLock()
    }

    private cleanup(): void {
        this.releaseLock()
        this.listenerGroup.removeAll()
        this.numFailedAttempts = 0
        removeEventListenerPoly("pageshow", window, this.onPageshow)
        removeEventListenerPoly("pagehide", window, this.onPagehide)
    }

    private isSupported(): boolean {
        return "wakeLock" in navigator
    }
}
